import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { searchAdvisers } from '../api/advisers';
import SiteLayout from '../components/SiteLayout';
import { useLocale } from '../hooks/useLocale';

function AdviserCard({ adviser, labels }) {
  const name = [adviser.name, adviser.lastN].filter(Boolean).join(' ');
  const to = `/asesor/${encodeURIComponent(adviser.publicId)}`;

  return (
    <Link to={to} className="adviser-card">
      <div className="adviser-card__media">
        {adviser.image ? (
          <img src={adviser.image} alt="" loading="lazy" />
        ) : (
          <span className="adviser-card__initials" aria-hidden="true">
            {(adviser.name || '?').charAt(0).toUpperCase()}
          </span>
        )}
      </div>
      <div className="adviser-card__body">
        <div className="adviser-card__title-row">
          <h2>{name || adviser.username}</h2>
          {adviser.adviserVerified ? (
            <span className="adviser-badge">{labels.verified}</span>
          ) : null}
        </div>
        {adviser.expertise ? <p className="adviser-card__expertise">{adviser.expertise}</p> : null}
        <div className="adviser-card__meta">
          {adviser.country ? <span>{adviser.country}</span> : null}
          {adviser.rating != null ? (
            <span>
              {labels.rating}: {Number(adviser.rating).toFixed(1)}
            </span>
          ) : null}
        </div>
        <span className="adviser-card__cta">{labels.viewProfile}</span>
      </div>
    </Link>
  );
}

export default function AdvisorSearchPage() {
  const { t } = useLocale();
  const labels = t.search;
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();
  const [state, setState] = useState({ status: 'idle', items: [], total: 0 });

  useEffect(() => {
    if (!q) {
      setState({ status: 'empty-query', items: [], total: 0 });
      return undefined;
    }

    let cancelled = false;
    setState((prev) => ({ ...prev, status: 'loading' }));

    searchAdvisers(q)
      .then((result) => {
        if (cancelled) return;
        setState({
          status: result.items.length ? 'ready' : 'empty',
          items: result.items,
          total: result.total,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState({
          status: 'error',
          items: [],
          total: 0,
          message: error.message || labels.error,
        });
      });

    return () => {
      cancelled = true;
    };
  }, [q, labels.error]);

  return (
    <SiteLayout t={t}>
      <main id="contenido" className="adviser-search-page">
        <section className="adviser-search-page__hero">
          <div className="shell">
            <p className="eyebrow">{labels.title}</p>
            <h1>
              {q ? (
                <>
                  {labels.resultsFor} <span className="accent-line">“{q}”</span>
                </>
              ) : (
                labels.title
              )}
            </h1>
            {state.status === 'ready' ? (
              <p className="adviser-search-page__count">
                {labels.resultCount.replace('{count}', String(state.total))}
              </p>
            ) : null}
          </div>
        </section>

        <section className="shell adviser-search-page__body">
          {state.status === 'empty-query' ? (
            <p className="adviser-search-page__status">{labels.emptyQuery}</p>
          ) : null}
          {state.status === 'loading' ? (
            <p className="adviser-search-page__status">{labels.loading}</p>
          ) : null}
          {state.status === 'empty' ? (
            <p className="adviser-search-page__status">{labels.empty}</p>
          ) : null}
          {state.status === 'error' ? (
            <div className="adviser-search-page__status adviser-search-page__status--error">
              <p>{state.message || labels.error}</p>
            </div>
          ) : null}
          {state.status === 'ready' ? (
            <div className="adviser-grid">
              {state.items.map((adviser) => (
                <AdviserCard
                  key={adviser.publicId}
                  adviser={adviser}
                  labels={labels}
                />
              ))}
            </div>
          ) : null}
        </section>
      </main>
    </SiteLayout>
  );
}
